/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import * as stores from "stores";
import { triggerOpenDocumentInDesktopEvent, triggerUnsupportedFeatureFoundEvent } from "publicApis/Events/events";
import { getIntl } from "selectors/appSelectors";
import {
  logUnsupportedWidgetShown,
  logUnsupportedWidgetClosed,
  logUnsupportedWidgetOpenInDesktopClicked
} from "logging";
import {
  getShouldShowUnsupportedFieldAlert,
  getDisplayOpenInDesktopOnUnsupportedNotification
} from "selectors/documentSelectors";
import { getDigSigBannerVisibility, getPdfValidationSummary } from "selectors/digSigSelectors";
import { VALIDATION_STATUS, NOTIFICATION_TYPE } from "constants/constants";
import { rhpDropinIds } from "constants/enums";
import { apiFactory } from "api/external/ApiFactory";
import { publicApiNames } from "publicApis/constants";
import DigSigCss from "components/digsig/DigSig.css";
import {
  trackDigSigBannerShow,
  trackDigSigBannerShowMoreCTAClick,
  trackDigSigBannerClose
} from "analytics/digSigEvents";

export const showUnsupportedWidgetNotification = data => {
  if (getShouldShowUnsupportedFieldAlert(stores)) {
    const intl = getIntl(stores);
    stores.notificationViewStore.addNotification(intl.formatMessage({ id: "acroform.unsupported.message" }), {
      type: NOTIFICATION_TYPE.BANNER,
      actionLabel: getDisplayOpenInDesktopOnUnsupportedNotification(stores)
        ? intl.formatMessage({ id: "common.openInDesktop" })
        : null,
      onAction: () => {
        triggerOpenDocumentInDesktopEvent();
        logUnsupportedWidgetOpenInDesktopClicked();
      },
      onClose: () => {
        logUnsupportedWidgetClosed();
      }
    });
    stores.documentStore.setShowUnsupportedFieldAlert(false);

    logUnsupportedWidgetShown(data);
  }
  if (stores.documentStore.shouldRenderAcroForm && stores.documentStore.triggerUnsupportedFieldEvent) {
    triggerUnsupportedFeatureFoundEvent("forms", data);
    stores.documentStore.setTriggerUnsupportedFieldEvent(false);
  }
};

export const showDigSigBanner = () => {
  const pdfValidationSummary = getPdfValidationSummary(stores);
  if (!pdfValidationSummary) {
    return;
  }

  const { message, status, unsupportedSignatures } = pdfValidationSummary;
  const intl = getIntl(stores);

  /**
   * don't show banner if
   * 1. if banner visibility is disabled
   * 2. full screen mode is enabled
   * 3. all signatures are valid
   */
  if (!getDigSigBannerVisibility(stores) || status === VALIDATION_STATUS.VALID) {
    return;
  }

  const hasUnsupportedSignatures = unsupportedSignatures.length > 0;
  let ctaClicked = false;

  const openDigSigRHP = async () => {
    ctaClicked = true;
    const rhpApis = await apiFactory.getExposedApis("preview", [publicApiNames.RHP], true, true);
    rhpApis[publicApiNames.RHP].openPanel(rhpDropinIds.DIGITAL_SIGNATURES);
    trackDigSigBannerShowMoreCTAClick();
  };

  const bannerId = stores.notificationViewStore.addNotification(message, {
    overrideClassName: DigSigCss.banner,
    shouldRemoveActiveBanners: true,
    type: NOTIFICATION_TYPE.BANNER,
    variant: "info",
    actionLabel: hasUnsupportedSignatures ? null : intl.formatMessage({ id: "modernviewer.showMore.label" }),
    onAction: hasUnsupportedSignatures ? null : openDigSigRHP,
    closeOnAction: true,
    onClose: () => {
      stores.digSigStore.setDigSigBannerId(null);
      trackDigSigBannerClose(ctaClicked ? "Show More button Click" : "CrossIcon Click");
    }
  });

  stores.digSigStore.setDigSigBannerId(bannerId);
  trackDigSigBannerShow();
};
